import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from '../services/supabaseClient';

// 1. Crear el Contexto
const ChatStateContext = createContext();

// Clave para sessionStorage
const PROCESSING_STATUS_KEY = 'chatProcessingStatus';

// 2. Crear el Proveedor
export const ChatStateProvider = ({ children }) => {
  // Inicializar el estado leyendo desde sessionStorage
  const [processingStatus, setProcessingStatus] = useState(() => {
    try {
      // 💾 WINDOWS DEBUG: Test sessionStorage availability
      console.log('💾 SESSIONSTORAGE READ - Windows debug:', {
        available: typeof sessionStorage !== 'undefined',
        timestamp: new Date().toISOString(),
        platform: navigator.platform,
        cookiesEnabled: navigator.cookieEnabled
      });

      const storedStatus = sessionStorage.getItem(PROCESSING_STATUS_KEY);
      const parsed = storedStatus ? JSON.parse(storedStatus) : {};

      console.log('💾 SESSIONSTORAGE INITIAL VALUE:', {
        found: !!storedStatus,
        value: parsed,
        timestamp: new Date().toISOString()
      });

      return parsed;
    } catch (error) {
      console.error("❌ Error al leer processingStatus desde sessionStorage:", error);
      console.error("❌ Error details - Windows debug:", {
        errorName: error.name,
        errorMessage: error.message,
        errorStack: error.stack?.substring(0, 200)
      });
      return {}; // Devolver valor por defecto en caso de error
    }
  });

  // Guardar en sessionStorage cada vez que processingStatus cambie
  useEffect(() => {
    try {
      const jsonValue = JSON.stringify(processingStatus);
      sessionStorage.setItem(PROCESSING_STATUS_KEY, jsonValue);

      // 💾 WINDOWS DEBUG: Confirm write success
      console.log('💾 SESSIONSTORAGE WRITE - Windows debug:', {
        success: true,
        processingStatus,
        jsonLength: jsonValue.length,
        threadIds: Object.keys(processingStatus),
        timestamp: new Date().toISOString()
      });

      // Verify write by reading back
      const readBack = sessionStorage.getItem(PROCESSING_STATUS_KEY);
      if (readBack !== jsonValue) {
        console.error('❌ SESSIONSTORAGE WRITE VERIFICATION FAILED - Windows debug:', {
          written: jsonValue,
          readBack,
          match: false
        });
      } else {
        console.log('✅ SESSIONSTORAGE WRITE VERIFIED');
      }
    } catch (error) {
      console.error("❌ Error al guardar processingStatus en sessionStorage:", error);
      console.error("❌ Write error details - Windows debug:", {
        errorName: error.name,
        errorMessage: error.message,
        processingStatus,
        quotaExceeded: error.name === 'QuotaExceededError'
      });
    }
  }, [processingStatus]); // Dependencia: se ejecuta cuando processingStatus cambia

  // 🔐 SECURITY: Clear state on logout to prevent data leakage between users
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        console.log('[ChatStateContext] 🔐 Clearing processing status on logout');
        setProcessingStatus({});
        sessionStorage.removeItem(PROCESSING_STATUS_KEY);
      }
    });

    return () => subscription?.unsubscribe();
  }, []);

  // Podríamos añadir más lógica aquí si fuera necesario, 
  // por ejemplo, para inicializar el estado desde localStorage si quisiéramos persistencia total.
  // Por ahora, solo gestiona el estado en memoria mientras el proveedor esté montado.

  // ✅ FIX: Wrap value in useMemo to prevent unnecessary re-renders of consumers
  // Without this, a new object is created on every render, triggering all consumers to re-render
  const value = React.useMemo(() => ({
    processingStatus,
    setProcessingStatus
  }), [processingStatus]); // setProcessingStatus is stable from useState

  return (
    <ChatStateContext.Provider value={value}>
      {children}
    </ChatStateContext.Provider>
  );
};

// 3. Crear un Hook personalizado para usar el Contexto (opcional pero buena práctica)
export const useChatState = () => {
  const context = useContext(ChatStateContext);
  if (context === undefined) {
    throw new Error('useChatState debe ser usado dentro de un ChatStateProvider');
  }
  return context;
}; 